import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Bell, Minus, Plus, ShieldCheck, Ticket } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../api";
import { useAuth } from "../auth";
import { useLang } from "../i18n";
import { Reveal, SectionHead } from "../components/Section";
import { MobileMoneyPanel, PaymentMethodPicker } from "../components/MobileMoney";
import { ct } from "../checkoutI18n";

const inputCls = "w-full rounded-xl border border-lakou-gold/25 bg-lakou-raise/70 px-4 py-3 text-sm text-lakou-cream placeholder:text-lakou-sand focus:outline-none focus:border-lakou-gold transition-colors";

export default function Tickets() {
    const { user } = useAuth();
    const { t } = useLang();
    const [cats, setCats] = useState([]);
    const [selected, setSelected] = useState(null);
    const [qty, setQty] = useState(1);
    const [form, setForm] = useState({ name: "", email: "", phone: "" });
    const [method, setMethod] = useState("card");
    const [mobileOrder, setMobileOrder] = useState(null);
    const [busy, setBusy] = useState(false);
    const [notifyEmail, setNotifyEmail] = useState("");
    const [notified, setNotified] = useState(false);
    const [quote, setQuote] = useState(null);
    const [manualLang, setManualLang] = useState(null);
    // MonCash/NatCash default checkout to Kreyòl; Stripe defaults to English; a manual pick always wins
    const coLang = manualLang || (method === "card" ? "en" : "ht");
    const C = (k) => ct(coLang, k);

    const anyOnSale = cats.some((c) => c.sales_open && c.price != null && !(c.remaining !== null && c.remaining <= 0));

    useEffect(() => {
        if (!selected) { setQuote(null); return; }
        let on = true;
        api.post("/tickets/quote", { category_id: selected.id, quantity: qty, method })
            .then((r) => { if (on) setQuote(r.data); })
            .catch(() => { if (on) setQuote(null); });
        return () => { on = false; };
    }, [selected, qty, method]);

    const notify = async (e) => {
        e.preventDefault();
        try {
            await api.post("/newsletter", { email: notifyEmail, source: "tickets_drop" });
            setNotified(true);
            toast.success(t("notify_thanks"));
        } catch (err) {
            toast.error(apiError(err));
        }
    };

    useEffect(() => {
        api.get("/tickets/categories").then((r) => {
            const list = r.data.categories || [];
            setCats(list);
            const onSale = (c) => c.sales_open && c.price != null && !(c.remaining !== null && c.remaining <= 0);
            setSelected(list.find((c) => c.id === "general" && onSale(c)) || list.find(onSale) || null);
        }).catch(() => {});
    }, []);

    useEffect(() => {
        if (user?.email) setForm((f) => ({ ...f, email: user.email, name: f.name || user.name || "" }));
    }, [user]);

    const buy = async (e) => {
        e.preventDefault();
        if (!selected) return toast.error(C("select_first"));
        setBusy(true);
        try {
            if (method === "card" || selected.price === 0) {
                const { data } = await api.post("/tickets/checkout", {
                    category_id: selected.id, quantity: qty, ...form,
                    origin_url: window.location.origin,
                });
                window.location.href = data.checkout_url;
            } else {
                const { data } = await api.post("/mobile/checkout", {
                    order_type: "ticket", method, category_id: selected.id, quantity: qty, ...form,
                    origin_url: window.location.origin,
                });
                setMobileOrder({ ...data, email: form.email, order_type: "ticket" });
                setBusy(false);
                window.scrollTo({ top: 0, behavior: "smooth" });
            }
        } catch (err) {
            toast.error(apiError(err, C("err_checkout")));
            setBusy(false);
        }
    };

    return (
        <div data-testid="tickets-page" className="pt-32 pb-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <SectionHead eyebrow={t("hero_overline")} title={t("tickets_title")} testId="tickets-title" />
                <div className="mt-14 grid grid-cols-1 lg:grid-cols-5 gap-10">
                    <div className="lg:col-span-3 space-y-5">
                        <div className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-lakou-sand" data-testid="ticket-type-label">{C("ticket_type")}</div>
                        {cats.map((c, i) => {
                            const active = selected?.id === c.id;
                            const open = c.sales_open && c.price != null;
                            const soldOut = c.remaining !== null && c.remaining <= 0;
                            return (
                                <Reveal key={c.id} delay={i * 0.07}>
                                    <button
                                        type="button"
                                        disabled={!open || soldOut}
                                        onClick={() => setSelected(c)}
                                        data-testid={`ticket-category-${c.id}`}
                                        className={`w-full text-left rounded-3xl border p-6 sm:p-8 transition-all duration-300 ${
                                            active ? "border-lakou-gold bg-lakou-gold/10 tracer-border" : "border-lakou-gold/15 bg-lakou-coal/60 hover:border-lakou-gold/40"
                                        } ${(!open || soldOut) ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                                    >
                                        <div className="flex flex-wrap items-start justify-between gap-4">
                                            <div>
                                                <div className="flex items-center gap-3">
                                                    <Ticket size={18} className={active ? "text-lakou-gold" : "text-lakou-sand"} />
                                                    <span className="font-display font-extrabold text-xl text-lakou-cream">{c.name}</span>
                                                </div>
                                                <p className="mt-2 text-sm text-lakou-grey max-w-md">{c.description}</p>
                                                {c.remaining !== null && (
                                                    <p className="mt-2 font-mono2 text-[10px] uppercase tracking-[0.2em] text-lakou-sand">
                                                        {soldOut ? t("sold_out") : `${c.remaining} ${t("remaining")}`}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="text-right">
                                                <div className="font-display font-black text-3xl text-goldgrad" data-testid={`ticket-price-${c.id}`}>
                                                    {c.display_price != null ? (c.display_price === 0 ? t("free_label") : `$${c.display_price.toFixed(2)}`) : "TBA"}
                                                </div>
                                                <div className="mt-1 font-mono2 text-[10px] uppercase tracking-[0.2em] text-lakou-sand">
                                                    {!open ? t("sale_soon") : soldOut ? t("sold_out") : active ? t("selected") : t("tap_select")}
                                                </div>
                                            </div>
                                        </div>
                                    </button>
                                </Reveal>
                            );
                        })}
                    </div>

                    <Reveal delay={0.15} className="lg:col-span-2">
                        {mobileOrder ? (
                            <MobileMoneyPanel order={mobileOrder} onReset={() => setMobileOrder(null)} lang={coLang} />
                        ) : cats.length > 0 && !anyOnSale ? (
                            <div className="rounded-3xl border border-lakou-gold/25 bg-lakou-coal/70 p-7 sm:p-8 space-y-5 lg:sticky lg:top-28" data-testid="ticket-notify-card">
                                <div className="w-12 h-12 rounded-full bg-lakou-gold/15 border border-lakou-gold/30 flex items-center justify-center">
                                    <Bell size={20} className="text-lakou-gold" />
                                </div>
                                <div className="font-display font-extrabold text-xl text-lakou-cream">{t("notify_title")}</div>
                                <p className="text-sm text-lakou-grey leading-relaxed">{t("notify_sub")}</p>
                                {notified ? (
                                    <p className="rounded-xl border border-lakou-gold/30 bg-lakou-gold/10 px-4 py-3 text-sm text-lakou-gold" data-testid="ticket-notify-success">{t("notify_thanks")}</p>
                                ) : (
                                    <form onSubmit={notify} className="space-y-3">
                                        <input required type="email" placeholder={t("form_email")} value={notifyEmail} onChange={(e) => setNotifyEmail(e.target.value)} data-testid="ticket-notify-email" className={inputCls} />
                                        <motion.button whileTap={{ scale: 0.98 }} type="submit" data-testid="ticket-notify-submit"
                                            className="w-full rounded-full bg-lakou-orange hover:bg-lakou-gold text-white hover:text-lakou-ink font-display font-bold text-xs uppercase tracking-[0.15em] py-4 transition-colors duration-300">
                                            {t("notify_btn")}
                                        </motion.button>
                                    </form>
                                )}
                            </div>
                        ) : (
                            <form onSubmit={buy} className="rounded-3xl border border-lakou-gold/20 bg-lakou-coal/70 p-7 sm:p-8 space-y-5 lg:sticky lg:top-28" data-testid="ticket-checkout-form">
                                <div className="flex items-center justify-between gap-3 flex-wrap">
                                    <div className="font-display font-extrabold text-lg text-lakou-cream">{C("complete_purchase")}</div>
                                    <div className="flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.1em]" data-testid="checkout-lang-selector">
                                        <button type="button" onClick={() => setManualLang("en")} data-testid="checkout-lang-en"
                                            className={`transition-colors ${coLang === "en" ? "text-lakou-gold" : "text-lakou-sand hover:text-lakou-cream"}`}>🇺🇸 English</button>
                                        <span className="text-lakou-sand">|</span>
                                        <button type="button" onClick={() => setManualLang("ht")} data-testid="checkout-lang-ht"
                                            className={`transition-colors ${coLang === "ht" ? "text-lakou-gold" : "text-lakou-sand hover:text-lakou-cream"}`}>🇭🇹 Kreyòl</button>
                                    </div>
                                </div>
                                <PaymentMethodPicker method={method} setMethod={setMethod} lang={coLang} />
                                <div className="flex items-center justify-between rounded-2xl border border-lakou-gold/15 bg-lakou-raise/60 px-4 py-3">
                                    <span className="text-sm text-lakou-grey">{C("qty")}</span>
                                    <div className="flex items-center gap-3">
                                        <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="qty-minus" aria-label="Decrease quantity" className="w-8 h-8 rounded-full border border-lakou-gold/25 flex items-center justify-center text-lakou-grey hover:text-lakou-gold"><Minus size={14} /></button>
                                        <span className="font-display font-bold text-lakou-cream w-6 text-center" data-testid="qty-value">{qty}</span>
                                        <button type="button" onClick={() => setQty((q) => Math.min(20, q + 1))} data-testid="qty-plus" aria-label="Increase quantity" className="w-8 h-8 rounded-full border border-lakou-gold/25 flex items-center justify-center text-lakou-grey hover:text-lakou-gold"><Plus size={14} /></button>
                                    </div>
                                </div>
                                <input required placeholder={C("full_name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ticket-name" className={inputCls} />
                                <input required type="email" placeholder={C("email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="ticket-email" className={inputCls} />
                                <input placeholder={C("phone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="ticket-phone" className={inputCls} />
                                <div className="flex justify-between font-mono2 text-sm border-t border-lakou-gold/15 pt-4">
                                    <span className="text-lakou-grey uppercase tracking-widest text-xs">{C("total")}</span>
                                    <span className="text-lakou-gold" data-testid="ticket-total">
                                        {quote ? (quote.payable_total === 0 ? t("free_label") : `$${quote.payable_total.toFixed(2)}`) : "—"}
                                    </span>
                                </div>
                                {quote && method === "card" && quote.processing_included > 0 && (
                                    <p className="text-right font-mono2 text-[10px] uppercase tracking-[0.15em] text-lakou-sand -mt-3" data-testid="processing-note">
                                        {C("processing_note")} (${quote.processing_included.toFixed(2)})
                                    </p>
                                )}
                                {quote && method !== "card" && quote.amount_htg != null && (
                                    <p className="text-right font-mono2 text-[10px] uppercase tracking-[0.15em] text-lakou-sand -mt-3" data-testid="htg-note">
                                        ≈ {quote.amount_htg.toLocaleString()} HTG
                                    </p>
                                )}
                                <motion.button
                                    whileTap={{ scale: 0.98 }}
                                    type="submit"
                                    disabled={busy}
                                    data-testid="ticket-checkout-submit"
                                    className="w-full rounded-full bg-lakou-orange hover:bg-lakou-gold text-white hover:text-lakou-ink font-display font-bold text-xs uppercase tracking-[0.15em] py-4 transition-colors duration-300 disabled:opacity-50"
                                >
                                    {busy ? "…" : selected
                                        ? selected.display_price === 0
                                            ? C("get_free")
                                            : method === "card"
                                                ? `${C("continue_card")} — ${selected.name}`
                                                : `${method === "moncash" ? "MonCash" : "NatCash"} — ${selected.name}`
                                        : C("select_first")}
                                </motion.button>
                                <p className="flex items-center gap-2 text-[11px] text-lakou-sand justify-center">
                                    <ShieldCheck size={13} className="text-lakou-gold" />
                                    {selected?.display_price === 0 ? C("free_note") : method === "card" ? C("secure_note") : C("mm_note")}
                                </p>
                            </form>
                        )}
                    </Reveal>
                </div>
            </div>
        </div>
    );
}
